import { Controller } from "@hotwired/stimulus"

// Handles password change form validation and UI
export default class extends Controller {
  static targets = [
    "currentPassword",
    "newPassword",
    "confirmPassword",
    "passwordSection",
    "toggleButton",
    "strengthIndicator"
  ]

  connect() {
    // Hide password fields initially if they're all empty
    this.checkInitialState()
  }

  checkInitialState() {
    if (this.hasCurrentPasswordTarget &&
        this.hasNewPasswordTarget &&
        this.hasConfirmPasswordTarget) {

      const allEmpty = !this.currentPasswordTarget.value &&
                       !this.newPasswordTarget.value &&
                       !this.confirmPasswordTarget.value

      if (allEmpty && this.hasPasswordSectionTarget) {
        // Start with password section hidden
        this.passwordSectionTarget.style.display = 'none'

        // Add a toggle button if it doesn't exist
        if (!this.hasToggleButtonTarget) {
          this.addToggleButton()
        }
      }
    }
  }

  addToggleButton() {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'text-amber-600 hover:text-amber-700 font-medium text-sm underline mb-4'
    button.textContent = 'Change Password'
    button.setAttribute('data-password-change-target', 'toggleButton')
    button.setAttribute('data-action', 'click->password-change#togglePasswordFields')

    if (this.hasPasswordSectionTarget) {
      this.passwordSectionTarget.parentElement.insertBefore(button, this.passwordSectionTarget)
    }
  }

  togglePasswordFields(event) {
    event.preventDefault()

    if (this.hasPasswordSectionTarget) {
      const isHidden = this.passwordSectionTarget.style.display === 'none'

      if (isHidden) {
        this.passwordSectionTarget.style.display = 'block'
        if (this.hasToggleButtonTarget) {
          this.toggleButtonTarget.textContent = 'Cancel Password Change'
          this.toggleButtonTarget.className = 'text-red-600 hover:text-red-700 font-medium text-sm underline mb-4'
        }
        // Focus on current password field
        if (this.hasCurrentPasswordTarget) {
          this.currentPasswordTarget.focus()
        }
      } else {
        // Clear password fields when canceling
        this.clearPasswordFields()
        this.passwordSectionTarget.style.display = 'none'
        if (this.hasToggleButtonTarget) {
          this.toggleButtonTarget.textContent = 'Change Password'
          this.toggleButtonTarget.className = 'text-amber-600 hover:text-amber-700 font-medium text-sm underline mb-4'
        }
      }
    }
  }

  clearPasswordFields() {
    if (this.hasCurrentPasswordTarget) this.currentPasswordTarget.value = ''
    if (this.hasNewPasswordTarget) this.newPasswordTarget.value = ''
    if (this.hasConfirmPasswordTarget) this.confirmPasswordTarget.value = ''
    this.clearValidationMessages()
  }

  validatePasswords() {
    // Only validate if user is changing password
    if (!this.hasNewPasswordTarget || !this.newPasswordTarget.value) {
      return
    }

    const newPassword = this.newPasswordTarget.value
    const confirmPassword = this.hasConfirmPasswordTarget ? this.confirmPasswordTarget.value : ''

    // Check password strength
    this.checkPasswordStrength(newPassword)

    // Check if passwords match
    if (confirmPassword && newPassword !== confirmPassword) {
      this.showError(this.confirmPasswordTarget, "Passwords don't match")
    } else if (confirmPassword && newPassword === confirmPassword) {
      this.clearError(this.confirmPasswordTarget)
      this.showSuccess(this.confirmPasswordTarget, "Passwords match")
    }

    // Check if current password is provided
    if (this.hasCurrentPasswordTarget && !this.currentPasswordTarget.value && newPassword) {
      this.showError(this.currentPasswordTarget, "Current password is required to change password")
    } else if (this.hasCurrentPasswordTarget && this.currentPasswordTarget.value) {
      this.clearError(this.currentPasswordTarget)
    }
  }

  checkPasswordStrength(password) {
    if (!this.hasStrengthIndicatorTarget) return

    let strength = 0
    let message = ''

    // Check length
    if (password.length >= 8) strength++
    if (password.length >= 12) strength++

    // Check for character types
    if (/[a-z]/.test(password)) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^a-zA-Z0-9]/.test(password)) strength++

    // Set message and color based on strength
    const colors = ['red', 'orange', 'yellow', 'lime', 'green', 'emerald']
    const messages = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong']

    const color = colors[Math.min(strength - 1, colors.length - 1)] || 'red'
    message = messages[Math.min(strength - 1, messages.length - 1)] || 'Very Weak'

    this.strengthIndicatorTarget.innerHTML = `
      <span class="text-${color}-600 font-medium">
        Password Strength: ${message}
      </span>
    `
  }

  showError(target, message) {
    // Remove any existing messages
    this.clearError(target)

    // Add error styling
    target.classList.add('border-red-500')

    // Add error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'text-red-500 text-sm mt-1 password-validation-message'
    errorDiv.textContent = message
    target.parentElement.appendChild(errorDiv)
  }

  showSuccess(target, message) {
    // Remove any existing messages
    this.clearError(target)

    // Add success styling
    target.classList.add('border-green-500')

    // Add success message
    const successDiv = document.createElement('div')
    successDiv.className = 'text-green-500 text-sm mt-1 password-validation-message'
    successDiv.textContent = message
    target.parentElement.appendChild(successDiv)
  }

  clearError(target) {
    target.classList.remove('border-red-500', 'border-green-500')
    const messages = target.parentElement.querySelectorAll('.password-validation-message')
    messages.forEach(msg => msg.remove())
  }

  clearValidationMessages() {
    const messages = this.element.querySelectorAll('.password-validation-message')
    messages.forEach(msg => msg.remove())

    if (this.hasStrengthIndicatorTarget) {
      this.strengthIndicatorTarget.innerHTML = ''
    }
  }

  // Called when form is about to submit
  validateBeforeSubmit(event) {
    // If changing password, ensure all fields are filled
    if (this.hasNewPasswordTarget && this.newPasswordTarget.value) {
      if (!this.hasCurrentPasswordTarget || !this.currentPasswordTarget.value) {
        event.preventDefault()
        this.showError(this.currentPasswordTarget, "Current password is required")
        this.currentPasswordTarget.focus()
        return false
      }

      if (!this.hasConfirmPasswordTarget || !this.confirmPasswordTarget.value) {
        event.preventDefault()
        this.showError(this.confirmPasswordTarget, "Please confirm your new password")
        this.confirmPasswordTarget.focus()
        return false
      }

      if (this.newPasswordTarget.value !== this.confirmPasswordTarget.value) {
        event.preventDefault()
        this.showError(this.confirmPasswordTarget, "Passwords don't match")
        this.confirmPasswordTarget.focus()
        return false
      }
    }

    return true
  }
}